import { Button, Stack, Typography } from '@mui/material';
import z from 'zod';

import { useAppForm } from '@/hooks/useAppForm';
import { I18N_DE } from '@/i18n/de';
import type { CreateUserDto, Organization } from '@/modules/api/backend';
import { OrganizationUserRole, UserRole } from '@/modules/api/backend';
import { zOrganizationUserRole, zUserRole } from '@/modules/api/backend/zod.gen';

interface InviteUserFormProps {
  onSubmit: (values: CreateUserDto) => Promise<void>;
  onCancel: () => void;
  organizations: Organization[];
}
export function InviteUserForm({ onSubmit, onCancel, organizations }: InviteUserFormProps) {
  const form = useAppForm({
    defaultValues: { email: '', role: '', organizationId: '', organizationUserRole: '' },
    onSubmit: async ({ value }) => {
      await onSubmit({
        email: value.email,
        role: value.role as UserRole,
        ...(value.role === UserRole.USER && {
          organizationId: value.organizationId || undefined,
          organizationUserRole: (value.organizationUserRole as OrganizationUserRole) || undefined,
        }),
      });
    },
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h2">User*in hinzufügen</Typography>

        <form.AppField
          name="email"
          validators={{
            onChange: z.email(),
          }}
        >
          {(field) => <field.TextField label="E-Mail" type="email" />}
        </form.AppField>

        <form.AppField
          name="role"
          validators={{
            onChange: zUserRole,
          }}
        >
          {(field) => (
            <field.SelectField
              label="Globale Rolle"
              onChange={(event) => {
                const role = event.target.value as UserRole;
                field.handleChange(role);

                if (role !== UserRole.USER) {
                  form.setFieldValue('organizationId', '');
                  form.setFieldValue('organizationUserRole', OrganizationUserRole.EDITOR);
                }
              }}
              options={Object.entries(I18N_DE.UserRole).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          )}
        </form.AppField>

        <form.AppField
          name="organizationId"
          validators={{
            onChangeListenTo: ['role'],
            onChange: ({ value, fieldApi }) => {
              if (fieldApi.form.getFieldValue('role') !== UserRole.USER) return undefined;

              return z.string().nonempty().safeParse(value).error?.issues[0]?.message;
            },
          }}
        >
          {(field) => (
            <form.Subscribe selector={(s) => s.values.role === UserRole.USER}>
              {(isUser) => (
                <field.SelectField
                  disabled={!isUser}
                  label="Organisation"
                  options={organizations.map((org) => ({ value: org.id, label: org.name }))}
                  sx={{ mb: 1 }}
                />
              )}
            </form.Subscribe>
          )}
        </form.AppField>

        <form.AppField
          name="organizationUserRole"
          validators={{
            onChangeListenTo: ['role'],
            onChange: ({ value, fieldApi }) => {
              if (fieldApi.form.getFieldValue('role') !== UserRole.USER) return undefined;

              return zOrganizationUserRole.safeParse(value).error?.issues[0]?.message;
            },
          }}
        >
          {(field) => (
            <form.Subscribe selector={(state) => state.values.role === UserRole.USER && !!state.values.organizationId}>
              {(enabled) => (
                <field.SelectField
                  disabled={!enabled}
                  label="Organisationsrolle"
                  options={Object.entries(I18N_DE.OrganizationUserRole).map(([value, label]) => ({ value, label }))}
                />
              )}
            </form.Subscribe>
          )}
        </form.AppField>

        <Stack alignItems="center" direction="row" justifyContent="flex-end" spacing={2}>
          <Button color="primary" onClick={onCancel} variant="outlined">
            Abbrechen
          </Button>
          <Button type="submit" variant="contained">
            Einladen
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
